import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  MarketingProductMockById,
  MockInterviewQuestions,
  MockBrandedCareerPage,
  MockJobPositionAI,
  MockApplicationsScores,
  MockInterviewReport,
} from './MarketingProductMocks';
import { cn } from '@/lib/utils';

interface FeatureBlock {
  titleKey: string;
  bodyKey: string;
  mock: React.ReactNode;
  reverse?: boolean;
}

export function MarketingCoreFeatures() {
  const { t } = useLanguage();

  const blocks: FeatureBlock[] = [
    {
      titleKey: 'marketing.core.jd.title',
      bodyKey: 'marketing.core.jd.body',
      mock: <MockJobPositionAI />,
    },
    {
      titleKey: 'marketing.core.career.title',
      bodyKey: 'marketing.core.career.body',
      mock: <MockBrandedCareerPage />,
      reverse: true,
    },
    {
      titleKey: 'marketing.core.screening.title',
      bodyKey: 'marketing.core.screening.body',
      mock: <MockApplicationsScores />,
    },
    {
      titleKey: 'marketing.core.questions.title',
      bodyKey: 'marketing.core.questions.body',
      mock: <MockInterviewQuestions />,
      reverse: true,
    },
    {
      titleKey: 'marketing.core.interview.title',
      bodyKey: 'marketing.core.interview.body',
      mock: <MockInterviewReport />,
    },
  ];

  return (
    <section className="bg-background border-y border-border/60 py-16 md:py-24">
      <div className="container space-y-4 text-center max-w-3xl mx-auto mb-14">
        <h2 className="font-display font-bold text-2xl md:text-4xl tracking-tight">
          {t('marketing.core.sectionTitle')}
        </h2>
        <p className="text-muted-foreground leading-relaxed">{t('marketing.core.sectionSubtitle')}</p>
      </div>
      <div className="container space-y-20 md:space-y-28">
        {blocks.map((block) => (
          <div
            key={block.titleKey}
            className={cn(
              'grid lg:grid-cols-2 gap-10 items-center',
              block.reverse && 'lg:[&>*:first-child]:order-2',
            )}
          >
            <div className="space-y-4">
              <h3 className="font-display font-bold text-xl md:text-2xl">{t(block.titleKey)}</h3>
              <p className="text-muted-foreground leading-relaxed">{t(block.bodyKey)}</p>
            </div>
            <div>{block.mock}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function MarketingHumanLoopBand() {
  const { t } = useLanguage();
  return (
    <section className="container py-16 md:py-20">
      <div
        className="rounded-2xl p-8 md:p-12 text-center space-y-5 border border-brand-accent/20"
        style={{
          background: 'linear-gradient(135deg, rgba(124,58,237,0.08) 0%, rgba(13,148,136,0.06) 100%)',
        }}
      >
        <p className="text-sm font-medium text-brand-accent uppercase tracking-wide">
          {t('marketing.human.badge')}
        </p>
        <h2 className="font-display font-bold text-2xl md:text-3xl max-w-2xl mx-auto">
          {t('marketing.human.title')}
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">{t('marketing.human.body')}</p>
        <ul className="text-sm text-left max-w-xl mx-auto space-y-2 text-muted-foreground">
          {(['marketing.human.b1', 'marketing.human.b2', 'marketing.human.b3'] as const).map((key) => (
            <li key={key} className="flex gap-2">
              <span className="text-brand-accent font-bold shrink-0">✓</span>
              {t(key)}
            </li>
          ))}
        </ul>
        <Button size="lg" className="bg-brand-accent hover:bg-brand-accent/90" asChild>
          <Link to="/signup">{t('marketing.hero.ctaTrial')}</Link>
        </Button>
      </div>
    </section>
  );
}

/** Product tabs section — uses rich mocks per tab. */
export function MarketingProductTour() {
  const { t } = useLanguage();
  const tabs = [
    { id: 'positions' as const, label: t('marketing.product.tab.positions'), caption: t('marketing.product.caption.positions') },
    { id: 'detail' as const, label: t('marketing.product.tab.detail'), caption: t('marketing.product.caption.detail') },
    { id: 'applications' as const, label: t('marketing.product.tab.applications'), caption: t('marketing.product.caption.applications') },
    { id: 'report' as const, label: t('marketing.product.tab.report'), caption: t('marketing.product.caption.report') },
    { id: 'career' as const, label: t('marketing.product.tab.career'), caption: t('marketing.product.caption.career') },
  ];

  return (
    <section id="product" className="scroll-mt-24 container py-16 md:py-20">
      <p className="text-sm font-medium text-brand-accent uppercase tracking-wide mb-2">
        {t('marketing.product.eyebrow')}
      </p>
      <h2 className="font-display font-bold text-2xl md:text-3xl mb-2">{t('marketing.product.title')}</h2>
      <p className="text-muted-foreground mb-8 max-w-2xl leading-relaxed">{t('marketing.product.subtitle')}</p>
      <div className="flex flex-wrap gap-2 mb-6">
        {tabs.map((tab) => (
          <span
            key={tab.id}
            className="text-xs px-3 py-1.5 rounded-full border border-border/60 bg-muted/40 text-muted-foreground"
          >
            {tab.label}
          </span>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        {tabs.slice(0, 4).map((tab) => (
          <MarketingProductMockById key={tab.id} id={tab.id} caption={tab.caption} />
        ))}
        <div className="md:col-span-2 max-w-xl mx-auto w-full">
          <MarketingProductMockById id="career" caption={tabs[4].caption} />
        </div>
      </div>
      <div className="mt-10 flex justify-center">
        <Button size="lg" className="bg-brand-accent hover:bg-brand-accent/90" asChild>
          <Link to="/signup">{t('marketing.product.cta')}</Link>
        </Button>
      </div>
    </section>
  );
}
